import { useState } from "react";
import { toast } from "sonner";
import { Copy, Wallet, Landmark, Smartphone, ShieldCheck, Clock, XCircle, Printer, Lock, Loader2, Upload } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ReceiptPreview } from "../components/ReceiptPreview";
import { IdCard } from "../components/IdCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Inbox, useInbox } from "../components/Inbox";
import { BiometricCapture } from "../components/BiometricCapture";
import { useI18n } from "../i18n";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/providers";
import { api, formatError, fmtETB } from "../lib/api";
import { usePlans } from "../lib/plans";
import { TutorialGrid } from "../components/Tutorials";

const CopyRow = ({ label, value, testId }) => {
  const { t } = useI18n();
  return (
    <div className="flex items-center justify-between gap-3 border-b py-2.5 last:border-0">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="flex items-center gap-2 font-mono text-sm" data-testid={testId}>
        {value}
        <button onClick={() => { navigator.clipboard?.writeText(value); toast.success(t("copied")); }} aria-label={t("copy")} className="opacity-60 hover:opacity-100" data-testid={`${testId}-copy`}><Copy className="h-3.5 w-3.5" /></button>
      </span>
    </div>
  );
};

const StatusBanner = ({ user }) => {
  const { t } = useI18n();
  const { planName } = usePlans();
  const map = { pending: [Clock, "st_pending", "var(--gold)"], approved: [ShieldCheck, "st_approved", "var(--emerald)"], rejected: [XCircle, "st_rejected", "hsl(var(--destructive))"], blocked: [XCircle, "st_blocked", "hsl(var(--destructive))"] };
  const [Icon, key, color] = map[user.status];
  return (
    <div className="lux-card grid gap-6 p-7 md:grid-cols-4">
      <div className="md:col-span-2">
        <p className="eyebrow">{t("dash_welcome")}</p>
        <h1 className="mt-2 font-serif text-4xl font-bold" data-testid="dash-user-name">{user.full_name}</h1>
        {user.status === "rejected" && <p className="mt-2 text-sm text-destructive">{t("dash_rejected_msg")}</p>}
      </div>
      <div><p className="text-xs uppercase tracking-widest text-muted-foreground">{t("dash_status")}</p>
        <p className="mt-2 flex items-center gap-2 font-semibold" style={{ color }} data-testid="dash-status"><Icon className="h-4 w-4" />{t(key)}</p>
        <p className="mt-1 text-sm text-muted-foreground" data-testid="dash-payment-status">{t("dash_payment")}: {t(`pay_${user.payment_status}`)}</p></div>
      <div><p className="text-xs uppercase tracking-widest text-muted-foreground">{t("dash_package")}</p>
        <p className="mt-2 font-serif text-2xl font-semibold" style={{ color: "var(--gold)" }} data-testid="dash-package">{planName(user.package)}</p>
        {user.member_id && <p className="mt-1 font-mono text-xs" data-testid="dash-member-id">{user.member_id}</p>}</div>
    </div>
  );
};

const PaymentPanel = ({ user, onUpdate }) => {
  const { t } = useI18n();
  const { settings } = useSettings();
  const { getPlan, planName } = usePlans();
  const plan = getPlan(user.package) || { fee: 0, reward: 0 };
  const methods = settings?.payment_methods || [];
  const [method, setMethod] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!settings) return null;
  const chosen = method || methods[0]?.id || "";
  const submit = async () => {
    setBusy(true);
    const fd = new FormData();
    fd.append("method_id", chosen); fd.append("receipt", file);
    try { const { data } = await api.post("/me/payment", fd); onUpdate(data); setFile(null); toast.success(t("pay_receipt_submitted_msg")); }
    catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="lux-card p-7" data-testid="payment-panel">
      <div className="flex items-center gap-3"><Wallet className="h-5 w-5" style={{ color: "var(--gold)" }} /><h2 className="font-serif text-2xl font-semibold">{t("pay_title")}</h2></div>
      {user.payment_status === "paid" ? <p className="mt-4 text-sm" style={{ color: "var(--emerald)" }} data-testid="payment-paid-msg">{t("pay_paid_msg")}</p> : (
        <>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground" data-testid="payment-instruction">
            {t("pay_instruction", { tier: planName(user.package), fee: fmtETB(plan.fee) })}
          </p>
          <p className="mt-3 text-sm" data-testid="payment-reward"><span className="text-muted-foreground">{t("pk_reward")}:</span> <span className="font-serif text-lg font-semibold gold-text">{fmtETB(plan.reward)} ETB</span></p>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {methods.map((m) => (
              <div key={m.id} className="rounded-2xl border p-5" data-testid={`pay-method-card-${m.id}`}>
                <p className="mb-2 flex items-center gap-2 font-semibold">{m.phone && !m.account_number ? <Smartphone className="h-4 w-4" style={{ color: "var(--gold)" }} /> : <Landmark className="h-4 w-4" style={{ color: "var(--gold)" }} />}{m.name}</p>
                {m.account_name && <CopyRow label={t("pay_account_name")} value={m.account_name} testId={`pay-${m.id}-name`} />}
                {m.account_number && <CopyRow label={t("pay_account_number")} value={m.account_number} testId={`pay-${m.id}-number`} />}
                {m.phone && <CopyRow label={t("pay_phone")} value={m.phone} testId={`pay-${m.id}-phone`} />}
                {m.branch && <CopyRow label={t("pay_branch")} value={m.branch} testId={`pay-${m.id}-branch`} />}
              </div>
            ))}
          </div>
          {user.payment_status === "submitted" && (
            <div className="mt-6 flex items-center gap-4 rounded-xl p-3 text-sm" style={{ background: "var(--gold-soft)" }} data-testid="payment-submitted-msg">
              {user.receipt_path && <ReceiptPreview path={user.receipt_path} className="h-16 w-16" testId="my-receipt" />}
              <span>{t("pay_receipt_submitted_msg")}{user.payment_method && ` (${user.payment_method})`}</span>
            </div>
          )}
          {user.payment_status === "rejected" && <p className="mt-6 rounded-xl border border-destructive/50 p-3 text-sm text-destructive" data-testid="payment-rejected-msg">{t("pay_rejected_msg")}</p>}
          <div className="mt-6 grid items-end gap-4 md:grid-cols-[1fr_1fr_auto]">
            <div className="space-y-2"><Label>{t("pay_method")}</Label>
              <Select value={chosen} onValueChange={setMethod}>
                <SelectTrigger className="h-11" data-testid="pay-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>{methods.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`pay-method-option-${m.id}`}>{m.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-2"><Label>{t("pay_receipt")}</Label>
              <label className="btn-ghost-gold w-full cursor-pointer !py-2.5" data-testid="pay-receipt-upload-btn">
                <Upload className="h-4 w-4" /><span className="truncate">{file ? file.name : t("pay_upload_receipt")}</span>
                <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="pay-receipt-input" />
              </label></div>
            <button onClick={submit} disabled={busy || !file || !chosen} className="btn-gold" data-testid="pay-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("pay_submit_receipt")}</button>
          </div>
          {file?.type.startsWith("image/") && <img src={URL.createObjectURL(file)} alt="" className="mt-4 h-40 rounded-xl border object-contain" data-testid="pay-receipt-preview" />}
        </>
      )}
    </div>
  );
};

const ChangePassword = () => {
  const { t } = useI18n();
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.next.length < 6) return toast.error(t("err_password_len"));
    if (f.next !== f.confirm) return toast.error(t("err_password_match"));
    setBusy(true);
    try { await api.post("/auth/change-password", { current_password: f.current, new_password: f.next }); toast.success(t("cp_success")); setF({ current: "", next: "", confirm: "" }); }
    catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };
  const fields = [["current", "cp_current"], ["next", "cp_new"], ["confirm", "cp_confirm"]];
  return (
    <form onSubmit={submit} className="lux-card space-y-4 p-7" data-testid="change-password-form">
      <div className="flex items-center gap-3"><Lock className="h-5 w-5" style={{ color: "var(--gold)" }} /><h2 className="font-serif text-2xl font-semibold">{t("cp_title")}</h2></div>
      {fields.map(([k, l]) => (
        <div key={k} className="space-y-2"><Label>{t(l)}</Label><Input type="password" className="h-11" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} required data-testid={`cp-${k}-input`} /></div>
      ))}
      <button type="submit" disabled={busy} className="btn-gold w-full" data-testid="cp-submit-btn">{t("cp_btn")}</button>
    </form>
  );
};

export default function Dashboard() {
  const { t } = useI18n();
  const { user, setUser } = useAuth();
  const ready = user.status === "approved" && user.payment_status === "paid";
  const inbox = useInbox();
  const { settings } = useSettings();
  const saveBio = async (id) => { await api.post("/me/biometric", { credential_id: id }); setUser({ ...user, biometric_credential_id: id }); toast.success(t("saved")); };
  return (
    <section className="mx-auto max-w-7xl space-y-8 px-5 py-12 lg:px-8">
      <StatusBanner user={user} />
      <Tabs defaultValue="overview">
      <TabsList>
        <TabsTrigger value="overview" data-testid="dash-tab-overview">{t("dash_tab_overview")}</TabsTrigger>
        <TabsTrigger value="inbox" data-testid="dash-tab-inbox">{t("dash_tab_inbox")}
          {inbox.unread > 0 && <span className="ml-2 rounded-full px-1.5 text-[10px] font-bold" style={{ background: "var(--gold)", color: "#0A0D14" }} data-testid="dash-inbox-unread">{inbox.unread}</span>}
        </TabsTrigger>
        {settings?.tutorials_dashboard && <TabsTrigger value="tutorials" data-testid="dash-tab-tutorials">{t("dash_tab_tutorials")}</TabsTrigger>}
      </TabsList>
      {settings?.tutorials_dashboard && <TabsContent value="tutorials" className="mt-6"><h2 className="mb-6 font-serif text-2xl font-semibold">{t("tut_title")}</h2><TutorialGrid /></TabsContent>}
      <TabsContent value="inbox" className="mt-6"><h2 className="mb-4 font-serif text-2xl font-semibold">{t("inbox_title")}</h2><Inbox messages={inbox.messages} markRead={inbox.markRead} /></TabsContent>
      <TabsContent value="overview" className="mt-6 space-y-8">
      <div className="grid gap-8 lg:grid-cols-[2fr_1fr]">
        <PaymentPanel user={user} onUpdate={setUser} />
        <div className="lux-card grain relative flex h-fit flex-col justify-between overflow-hidden p-7" data-testid="financial-support-box">
          <p className="eyebrow">{t("dash_support")}</p>
          <p className="mt-6 font-serif text-6xl font-bold gold-text" data-testid="financial-support-amount">{fmtETB(user.financial_support)}</p>
          <p className="mt-2 text-sm text-muted-foreground">ETB · {t("dash_support_hint")}</p>
        </div>
      </div>
      <div className="grid gap-8 lg:grid-cols-[2fr_1fr]">
        <div className="lux-card p-7" data-testid="id-card-section">
          <h2 className="font-serif text-2xl font-semibold">{t("id_title")}</h2>
          {ready ? (
            <div className="mt-6 space-y-5">
              <div className="overflow-x-auto pb-2"><IdCard member={user} /></div>
              <button onClick={() => window.print()} className="btn-gold" data-testid="dash-print-id-btn"><Printer className="h-4 w-4" />{t("id_print")}</button>
            </div>
          ) : <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground" data-testid="id-card-locked"><Lock className="h-4 w-4" />{t("id_locked")}</p>}
          {!user.biometric_credential_id && (
            <div className="mt-8"><p className="mb-3 text-sm font-semibold">{t("bio_add")}</p><BiometricCapture captured={false} onCaptured={saveBio} userName={user.phone} displayName={user.full_name} /></div>
          )}
        </div>
        <ChangePassword />
      </div>
      </TabsContent>
      </Tabs>
    </section>
  );
}
